import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api, req } from '@/lib/api/client';
import { isApiError } from '@/lib/api/errors';
import { keys } from '@/lib/api/keys';
import { Button } from '@/components/Button';
import { Input, Label } from '@/components/Input';
import { Tabs } from '@/components/Misc';
import { IS_MOCK } from '@/config/constants';
import { AuthShell } from './AuthShell';
import { OAuthButtons, oidcErrorMessage } from './OAuthButtons';
import { safeNext } from './safeNext';

export function LoginPage({ mode: initialMode = 'login' }: { mode?: 'login' | 'signup' }) {
  const [mode, setMode] = useState<'login' | 'magic' | 'signup'>(initialMode);
  const [email, setEmail] = useState(IS_MOCK ? 'ana@acme.test' : '');
  const [password, setPassword] = useState(IS_MOCK ? 'password123' : '');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [params] = useSearchParams();
  const oidcError = params.get('error');
  const [error, setError] = useState<string | null>(oidcError ? oidcErrorMessage(oidcError) : null);
  const [sent, setSent] = useState(false);
  const next = safeNext(params.get('next'));
  const navigate = useNavigate();
  const qc = useQueryClient();

  // Mock mode stands in for the OIDC round-trip: the mock provider's start_url lands back here with
  // ?mock_oidc=1 and we "return from the callback" by signing in through a mock-only endpoint.
  const mockOidc = IS_MOCK && params.get('mock_oidc') === '1';
  useEffect(() => {
    if (!mockOidc) return;
    let cancelled = false;
    req(api.POST('/auth/login', { body: { email: 'ana@acme.test', password: 'password123' } }))
      .then(async () => {
        if (cancelled) return;
        await qc.invalidateQueries({ queryKey: keys.me() });
        navigate(next, { replace: true });
      })
      .catch(() => !cancelled && setError('Mock single sign-on failed'));
    return () => {
      cancelled = true;
    };
  }, [mockOidc, navigate, next, qc]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === 'login') {
        await req(api.POST('/auth/login', { body: { email, password } }));
      } else if (mode === 'signup') {
        await req(api.POST('/auth/signup', { body: { email, password, display_name: name || email.split('@')[0]! } }));
      } else {
        await req(api.POST('/auth/magic-link', { body: { email, redirect_path: next } }));
        setSent(true);
        return;
      }
      await qc.invalidateQueries({ queryKey: keys.me() });
      navigate(next, { replace: true });
    } catch (err) {
      setError(isApiError(err) ? err.message : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell title={mode === 'signup' ? 'Create your account' : 'Welcome back'} subtitle={mode === 'magic' ? 'We will email you a sign-in link.' : 'Sign in to your workspaces.'}>
      <Tabs value={mode} onChange={setMode} tabs={[{ id: 'login', label: 'Password' }, { id: 'magic', label: 'Magic link' }, { id: 'signup', label: 'Sign up' }]} className="mb-4" />
      {sent ? (
        <p className="rounded-md bg-accent-soft p-3 text-sm" role="status">
          Check <strong>{email}</strong> for a sign-in link. In mock mode any link under <code>/magic/&lt;token&gt;</code> works.
        </p>
      ) : (
        <form onSubmit={submit} className="space-y-3" aria-label={mode}>
          {mode === 'signup' && (
            <div>
              <Label htmlFor="name">Display name</Label>
              <Input id="name" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
          )}
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          {mode !== 'magic' && (
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" autoComplete={mode === 'signup' ? 'new-password' : 'current-password'} required minLength={mode === 'signup' ? 10 : 1} value={password} onChange={(e) => setPassword(e.target.value)} />
              {mode === 'signup' && <p className="mt-1 text-xs text-fg-subtle">At least 10 characters.</p>}
            </div>
          )}
          {error && (
            <p className="text-sm text-danger" role="alert">
              {error}
            </p>
          )}
          <Button type="submit" variant="primary" className="w-full" disabled={busy}>
            {busy ? 'Please wait…' : mode === 'login' ? 'Sign in' : mode === 'signup' ? 'Create account' : 'Send link'}
          </Button>
          {mode === 'login' && (
            <p className="text-center text-xs text-fg-subtle">
              <Link to="/reset/request" className="hover:underline">
                Forgot your password?
              </Link>
            </p>
          )}
          {IS_MOCK && mode === 'login' && <p className="text-center text-xs text-fg-subtle">Mock users (password123): mia@acme.test owner · ana@acme.test admin · kenji@acme.test editor · lena@acme.test commenter · tom@acme.test viewer</p>}
          {mode !== 'magic' && <OAuthButtons next={next} mode={mode === 'signup' ? 'signup' : 'login'} className="pt-1" />}
        </form>
      )}
    </AuthShell>
  );
}
